import { Link } from "react-router-dom";
import { PageShell } from "@/components/marketing/PageShell";
import { useSiteContent } from "@/hooks/useSiteContent";

const SHORT_CODE = "[SHORT CODE PENDING]";
const UPDATED = "November 2, 2026";

const FACTS = [["Short code", SHORT_CODE], ["Program", "ResidenceProof"], ["Frequency", "Varies"], ["Pricing", "Msg & data rates may apply"]];
const CARRIERS = ["AT&T", "T-Mobile", "Verizon Wireless", "US Cellular", "Boost Mobile", "Metro by T-Mobile", "Cricket Wireless", "Visible", "Mint Mobile", "Google Fi"];
const TYPES = [
  ["Account updates", "Sign-in alerts, invitation and approval notices, case status changes", "As needed"],
  ["Verification codes", "One-time two-factor sign-in codes (only if you agreed on the sign-in page)", "Each sign-in"],
  ["Consent requests", "A link asking a verification subject to review and respond, only for an open case", "Per open case"],
  ["Promotional", "Product news and offers (optional, unticked by default)", "Occasional"],
  ["Mixed marketing", "Campaigns combining news, events and offers (optional)", "Occasional"],
];

const H = ({ children }) => <h2 className="font-display text-xl font-bold text-oath-ink">{children}</h2>;
const P = ({ children }) => <p className="mt-3 text-sm leading-relaxed text-oath-secondary">{children}</p>;
const Sec = ({ id, children }) => <section id={id} className="mt-8 border-t border-gray-100 pt-6" data-testid={`sms-section-${id}`}>{children}</section>;
const Ul = ({ items }) => <ul className="mt-3 grid gap-1.5 pl-5 text-sm leading-relaxed text-oath-secondary [list-style:disc]">{items.map((i) => <li key={i}>{i}</li>)}</ul>;
const A = ({ to, children }) => <Link to={to} className="text-oath-accent underline">{children}</Link>;

const Facts = () => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="sms-facts">
    {FACTS.map(([k, v]) => (
      <div key={k} className="rounded-2xl border border-gray-200 bg-oath-bg/60 p-4">
        <div className="font-mono-label text-[9px] font-semibold text-oath-muted">{k.toUpperCase()}</div>
        <div className="mt-1 font-display text-base font-bold text-oath-ink">{v}</div>
      </div>
    ))}
  </div>
);

const TypesTable = () => (
  <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-200">
    <table className="w-full min-w-[560px] text-left text-sm" data-testid="sms-types-table">
      <thead className="bg-oath-bg/60 font-mono-label text-[9px] text-oath-muted"><tr><th className="px-4 py-3">MESSAGE TYPE</th><th className="px-4 py-3">DESCRIPTION</th><th className="px-4 py-3">FREQUENCY</th></tr></thead>
      <tbody>{TYPES.map(([t, d, f]) => (
        <tr key={t} className="border-t border-gray-100"><td className="px-4 py-3 font-medium text-oath-ink">{t}</td><td className="px-4 py-3 text-oath-secondary">{d}</td><td className="px-4 py-3 text-oath-secondary">{f}</td></tr>
      ))}</tbody>
    </table>
  </div>
);

const Box = ({ items }) => (
  <div className="mt-4 grid gap-3 sm:grid-cols-3">
    {items.map(([k, v]) => (
      <div key={k} className="rounded-2xl border border-gray-200 p-4">
        <div className="font-mono-label text-[9px] font-semibold text-oath-muted">{k}</div>
        <div className="mt-1 break-words text-sm font-semibold text-oath-ink">{v}</div>
      </div>
    ))}
  </div>
);

export default function MessagingTerms() {
  const { contact } = useSiteContent();
  const email = contact.support_email;
  const phone = contact.phone || "[PHONE NUMBER]";
  return (
    <PageShell eyebrow="— LEGAL —" title="SMS Terms">
      <article className="rounded-3xl border border-gray-200 bg-white p-6 sm:p-10" data-testid="messaging-terms">
        <div className="font-mono-label text-[9px] text-oath-muted" data-testid="sms-updated">LAST UPDATED {UPDATED.toUpperCase()}</div>
        <p className="mt-3 text-sm leading-relaxed text-oath-secondary">Terms and conditions for text-message (SMS) services provided by ResidenceProof.</p>
        <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">This document is provided as a draft and is not legal advice. Have it reviewed by qualified counsel before relying on it.</p>
        <div className="mt-6"><Facts /></div>

        <Sec id="opt-in"><H>User opt-in</H>
          <P>By ticking the optional text-message box on our <A to="/signin">sign-in page</A>, <A to="/opt-in">communication preferences page</A> or <A to="/request-access">access request form</A>, you consent to receive text messages from ResidenceProof at the number you provide, including:</P>
          <Ul items={["Account-related alerts and status updates", "Two-factor verification codes, if you agree to them", "Service reminders and updates", "Occasional promotional and marketing messages, only for the categories you choose"]} />
          <P><strong>Consent to receive text messages is not a condition of purchase</strong> or of using any ResidenceProof service. The box is always unticked by default.</P>
          <P>Verification subjects receive a consent-request text only when a business client opens a case and gives their number. That message is transactional and asks the subject to choose to sign or decline.</P>
          <P>Information collected during SMS opt-in, including your number and consent record, will <strong>not</strong> be sold, rented or shared with third parties for marketing purposes.</P>
        </Sec>

        <Sec id="opt-out"><H>User opt-out</H>
          <P>To stop receiving text messages at any time, reply <strong>STOP</strong> to any message or text STOP to {SHORT_CODE}. You may also reply QUIT, END, CANCEL, UNSUBSCRIBE, REVOKE, OPTOUT or STOP ALL.</P>
          <P>We will send one final message confirming your opt-out, and you will receive no further texts unless you opt in again. You can also change your choices on the <A to="/opt-in">communication preferences page</A> or by emailing {email}.</P>
        </Sec>

        <Sec id="help"><H>Help &amp; support</H>
          <P>For help with text messaging, reply <strong>HELP</strong> to any message or contact us:</P>
          <Box items={[["TEXT", `HELP to ${SHORT_CODE}`], ["EMAIL", email], ["PHONE", phone]]} />
        </Sec>

        <Sec id="fees"><H>User fees</H>
          <P>This is a standard-rate messaging service. <strong>Message and data rates may apply</strong> under your mobile carrier plan. ResidenceProof does not charge for the messages themselves.</P>
        </Sec>

        <Sec id="carriers"><H>Supported carriers (U.S. only)</H>
          <P>Our text messages are supported by most major U.S. carriers, including but not limited to:</P>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="sms-carriers">{CARRIERS.map((c) => <span key={c} className="rounded-full border border-gray-200 px-3 py-1 text-xs text-oath-ink">{c}</span>)}</div>
          <P>Carriers are not liable for delayed or undelivered messages.</P>
        </Sec>

        <Sec id="types"><H>Types of messages</H><TypesTable /></Sec>

        <Sec id="warranty"><H>Warranty disclaimer</H>
          <P>ResidenceProof is not responsible for delays in delivery, which depend on your wireless carrier's network. We make reasonable efforts to deliver messages promptly but cannot guarantee delivery times or delivery at all.</P>
          <P><strong>Text messages are a convenience and should not be your only way of tracking a case or account.</strong> Case status and notices are always available in your console and by email.</P>
        </Sec>

        <Sec id="privacy"><H>Privacy</H>
          <P>The phone number and information you give us for text messaging is used <strong>only</strong> to deliver the messages you requested and to manage your account or case. No mobile information will be shared with third parties or affiliates for marketing or promotional purposes; we share it only with service providers that deliver the messages, or when required by law.</P>
          <P>See our full <A to="/privacy">Privacy Policy</A> and, for California residents, our <A to="/california-privacy">California Privacy Notice</A>.</P>
        </Sec>

        <Sec id="disputes"><H>Dispute resolution</H>
          <P>Disputes arising from these SMS Terms are resolved as described in our <A to="/terms">Terms of Use</A>, including any arbitration and class-action waiver provisions set out there.</P>
        </Sec>

        <Sec id="misc"><H>Miscellaneous</H>
          <P>These SMS Terms, together with our <A to="/terms">Terms of Use</A> and <A to="/privacy">Privacy Policy</A>, form the entire agreement between you and ResidenceProof about text messaging. We post updates on this page with a new "last updated" date; continuing to receive messages after an update means you accept it. You can opt out at any time.</P>
        </Sec>

        <Sec id="contact"><H>Contact us</H>
          <P>Questions about these SMS Terms: <a href={`mailto:${email}`} className="text-oath-accent underline" data-testid="sms-contact-email">{email}</a> · {phone}</P>
        </Sec>
      </article>
    </PageShell>
  );
}
